import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { API_URL } from '../config'
import {
  PaintBrushIcon,
  PhotoIcon,
  ClockIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

interface BrandingData {
  _id?: string
  companyName: string
  chatbotName: string
  logo?: string
  primaryColor: string
  secondaryColor: string
  backgroundColor: string
  textColor: string
  greetingMessage: string
  welcomeMessage: string
  offlineMessage: string
  contactInfo: {
    email?: string
    phone?: string
    address?: string
    website?: string
    socialMedia?: {
      facebook?: string
      twitter?: string
      linkedin?: string
      instagram?: string
    }
  }
  workingHours: {
    enabled: boolean
    timezone: string
    schedule: {
      [key: string]: {
        start: string
        end: string
        enabled: boolean
      }
    }
  }
  customCSS?: string
  isActive?: boolean
  updatedBy?: any
  createdAt?: string
  updatedAt?: string
}

const DEFAULT_BRANDING: BrandingData = {
  companyName: 'VirtueNetz',
  chatbotName: 'Assistant',
  primaryColor: '#f37021',
  secondaryColor: '#e84d00',
  backgroundColor: '#FFFFFF',
  textColor: '#1c1919',
  greetingMessage: 'Hello! How can I help you today?',
  welcomeMessage: 'Welcome! I\'m here to assist you.',
  offlineMessage: 'We\'re currently offline. Please leave a message and we\'ll get back to you!',
  contactInfo: {
    socialMedia: {}
  },
  workingHours: {
    enabled: false,
    timezone: 'UTC',
    schedule: {
      monday: { start: '09:00', end: '17:00', enabled: true },
      tuesday: { start: '09:00', end: '17:00', enabled: true },
      wednesday: { start: '09:00', end: '17:00', enabled: true },
      thursday: { start: '09:00', end: '17:00', enabled: true },
      friday: { start: '09:00', end: '17:00', enabled: true },
      saturday: { start: '09:00', end: '17:00', enabled: false },
      sunday: { start: '09:00', end: '17:00', enabled: false },
    },
  },
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

const Branding: React.FC = () => {
  const [branding, setBranding] = useState<BrandingData>(DEFAULT_BRANDING)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'general' | 'colors' | 'messages' | 'contact' | 'hours' | 'advanced'>('general')
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    fetchBranding()
  }, [])

  const fetchBranding = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/branding`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      // Ensure contactInfo and socialMedia are initialized
      const brandingData = {
        ...response.data,
        contactInfo: {
          ...response.data.contactInfo,
          socialMedia: response.data.contactInfo?.socialMedia || {}
        }
      }

      setBranding(brandingData)
      if (response.data.logo) {
        setLogoPreview(`${API_URL.replace('/api', '')}${response.data.logo}`)
      }
    } catch (error) {
      console.error('Error fetching branding:', error)
      toast.error('Failed to load branding settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const token = localStorage.getItem('token')

      // Upload logo if changed
      if (logoFile) {
        const formData = new FormData()
        formData.append('logo', logoFile)

        const logoResponse = await axios.post(`${API_URL}/branding/logo`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        })

        branding.logo = logoResponse.data.logoUrl
        setLogoFile(null)
      }

      // Prepare data for API (exclude internal fields)
      const brandingData = {
        companyName: branding.companyName,
        chatbotName: branding.chatbotName,
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        backgroundColor: branding.backgroundColor,
        textColor: branding.textColor,
        greetingMessage: branding.greetingMessage,
        welcomeMessage: branding.welcomeMessage,
        offlineMessage: branding.offlineMessage,
        contactInfo: branding.contactInfo,
        workingHours: branding.workingHours,
        customCSS: branding.customCSS,
      }

      // Save branding settings
      if (branding._id) {
        await axios.put(`${API_URL}/branding/${branding._id}`, brandingData, {
          headers: { Authorization: `Bearer ${token}` },
        })
      } else {
        await axios.post(`${API_URL}/branding`, brandingData, {
          headers: { Authorization: `Bearer ${token}` },
        })
      }

      toast.success('Branding saved successfully')
      setHasChanges(false)
      fetchBranding()
    } catch (error: any) {
      console.error('Error saving branding:', error)
      toast.error(error.response?.data?.error || 'Failed to save branding')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset branding to default? This action cannot be undone.')) return

    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/branding/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      })
      toast.success('Branding reset to default')
      fetchBranding()
      setHasChanges(false)
    } catch (error) {
      console.error('Error resetting branding:', error)
      toast.error('Failed to reset branding')
    }
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file size must be less than 2MB')
        return
      }
      setLogoFile(file)
      setLogoPreview(URL.createObjectURL(file))
      setHasChanges(true)
    }
  }

  const handleRemoveLogo = async () => {
    if (!branding.logo) return

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/branding/logo`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setBranding({ ...branding, logo: undefined })
      setLogoPreview(null)
      setLogoFile(null)
      toast.success('Logo removed successfully')
    } catch (error) {
      console.error('Error removing logo:', error)
      toast.error('Failed to remove logo')
    }
  }

  const updateBranding = (updates: Partial<BrandingData>) => {
    setBranding({ ...branding, ...updates })
    setHasChanges(true)
  }

  const updateContactInfo = (field: string, value: string) => {
    setBranding({
      ...branding,
      contactInfo: {
        ...branding.contactInfo,
        socialMedia: branding.contactInfo?.socialMedia || {},
        [field]: value
      },
    })
    setHasChanges(true)
  }

  const updateSocialMedia = (platform: string, value: string) => {
    setBranding({
      ...branding,
      contactInfo: {
        ...branding.contactInfo,
        socialMedia: {
          ...branding.contactInfo?.socialMedia,
          [platform]: value
        },
      },
    })
    setHasChanges(true)
  }

  const updateWorkingHours = (updates: Partial<BrandingData['workingHours']>) => {
    setBranding({
      ...branding,
      workingHours: { ...branding.workingHours, ...updates },
    })
    setHasChanges(true)
  }

  const updateSchedule = (day: string, field: string, value: any) => {
    setBranding({
      ...branding,
      workingHours: {
        ...branding.workingHours,
        schedule: {
          ...branding.workingHours.schedule,
          [day]: { ...branding.workingHours.schedule[day], [field]: value },
        },
      },
    })
    setHasChanges(true)
  }

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading branding settings...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Branding & Customization</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Customize your chatbot's appearance and behavior
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleReset}
            className="inline-flex items-center px-4 py-2 border border-input rounded-md shadow-sm text-sm font-medium text-foreground bg-card hover:bg-muted/50"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Reset to Default
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                You have unsaved changes. Don't forget to save!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'general', name: 'General', icon: PaintBrushIcon },
            { id: 'colors', name: 'Colors', icon: PhotoIcon },
            { id: 'messages', name: 'Messages', icon: ChatBubbleLeftRightIcon },
            { id: 'contact', name: 'Contact Info', icon: GlobeAltIcon },
            { id: 'hours', name: 'Working Hours', icon: ClockIcon },
            { id: 'advanced', name: 'Advanced', icon: Cog6ToothIcon },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
              } group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <tab.icon className={`${activeTab === tab.id ? 'text-primary' : 'text-muted-foreground/70 group-hover:text-muted-foreground'} -ml-0.5 mr-2 h-5 w-5`} />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-card shadow rounded-lg p-6">
        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">General Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={branding.companyName}
                    onChange={(e) => updateBranding({ companyName: e.target.value })}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Chatbot Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={branding.chatbotName}
                    onChange={(e) => updateBranding({ chatbotName: e.target.value })}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="Assistant"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">The name displayed in the chat interface</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Logo
                  </label>
                  <div className="flex items-center space-x-4">
                    {logoPreview && (
                      <div className="relative">
                        <img src={logoPreview} alt="Logo" className="h-20 w-20 object-contain border border-input rounded" />
                        <button
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.svg,.gif"
                        onChange={handleLogoChange}
                        className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">JPG, PNG, SVG or GIF (max 2MB)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Colors Tab */}
        {activeTab === 'colors' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Color Scheme</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={branding.primaryColor}
                      onChange={(e) => updateBranding({ primaryColor: e.target.value })}
                      className="h-10 w-20 rounded border border-input cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.primaryColor}
                      onChange={(e) => updateBranding({ primaryColor: e.target.value })}
                      className="flex-1 border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="#f37021"
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Main theme color for buttons and accents</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={branding.secondaryColor}
                      onChange={(e) => updateBranding({ secondaryColor: e.target.value })}
                      className="h-10 w-20 rounded border border-input cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.secondaryColor}
                      onChange={(e) => updateBranding({ secondaryColor: e.target.value })}
                      className="flex-1 border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="#e84d00"
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Secondary accent color</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Background Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={branding.backgroundColor}
                      onChange={(e) => updateBranding({ backgroundColor: e.target.value })}
                      className="h-10 w-20 rounded border border-input cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.backgroundColor}
                      onChange={(e) => updateBranding({ backgroundColor: e.target.value })}
                      className="flex-1 border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="#FFFFFF"
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Chat widget background color</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Text Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={branding.textColor}
                      onChange={(e) => updateBranding({ textColor: e.target.value })}
                      className="h-10 w-20 rounded border border-input cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.textColor}
                      onChange={(e) => updateBranding({ textColor: e.target.value })}
                      className="flex-1 border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="#1c1919"
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Main text color in the chat</p>
                </div>
              </div>

              {/* Color Preview */}
              <div className="mt-6 p-4 border border-border rounded-lg">
                <h4 className="text-sm font-medium text-foreground mb-3">Preview</h4>
                <div className="space-y-3" style={{ backgroundColor: branding.backgroundColor }}>
                  <div className="flex items-center space-x-3 p-3">
                    <div
                      className="px-4 py-2 rounded-lg text-white"
                      style={{ backgroundColor: branding.primaryColor }}
                    >
                      Primary Button
                    </div>
                    <div
                      className="px-4 py-2 rounded-lg text-white"
                      style={{ backgroundColor: branding.secondaryColor }}
                    >
                      Secondary Button
                    </div>
                    <div
                      className="px-4 py-2"
                      style={{ color: branding.textColor }}
                    >
                      Sample Text
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Chat Messages</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Greeting Message <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={branding.greetingMessage}
                    onChange={(e) => updateBranding({ greetingMessage: e.target.value })}
                    maxLength={200}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="Hello! How can I help you today?"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">First message users see when opening the chat</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Welcome Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={branding.welcomeMessage}
                    onChange={(e) => updateBranding({ welcomeMessage: e.target.value })}
                    maxLength={300}
                    rows={3}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="Welcome! I'm here to assist you."
                  />
                  <p className="mt-1 text-xs text-muted-foreground">Introductory message shown to new users</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Offline Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={branding.offlineMessage}
                    onChange={(e) => updateBranding({ offlineMessage: e.target.value })}
                    maxLength={300}
                    rows={3}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="We're currently offline. Please leave a message and we'll get back to you!"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">Message shown when chatbot is outside working hours</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact Info Tab */}
        {activeTab === 'contact' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                    <input
                      type="email"
                      value={branding.contactInfo.email || ''}
                      onChange={(e) => updateContactInfo('email', e.target.value)}
                      className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Phone</label>
                    <input
                      type="tel"
                      value={branding.contactInfo.phone || ''}
                      onChange={(e) => updateContactInfo('phone', e.target.value)}
                      className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Address</label>
                  <input
                    type="text"
                    value={branding.contactInfo.address || ''}
                    onChange={(e) => updateContactInfo('address', e.target.value)}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="123 Main St, City, State 12345"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Website</label>
                  <input
                    type="url"
                    value={branding.contactInfo.website || ''}
                    onChange={(e) => updateContactInfo('website', e.target.value)}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="https://www.company.com"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Social Media</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Facebook</label>
                  <input
                    type="url"
                    value={branding.contactInfo.socialMedia?.facebook || ''}
                    onChange={(e) => updateSocialMedia('facebook', e.target.value)}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="https://facebook.com/yourpage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Twitter</label>
                  <input
                    type="url"
                    value={branding.contactInfo.socialMedia?.twitter || ''}
                    onChange={(e) => updateSocialMedia('twitter', e.target.value)}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="https://twitter.com/yourhandle"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">LinkedIn</label>
                  <input
                    type="url"
                    value={branding.contactInfo.socialMedia?.linkedin || ''}
                    onChange={(e) => updateSocialMedia('linkedin', e.target.value)}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="https://linkedin.com/company/yourcompany"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Instagram</label>
                  <input
                    type="url"
                    value={branding.contactInfo.socialMedia?.instagram || ''}
                    onChange={(e) => updateSocialMedia('instagram', e.target.value)}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder="https://instagram.com/yourprofile"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Working Hours Tab */}
        {activeTab === 'hours' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Working Hours</h3>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    id="enable-hours"
                    type="checkbox"
                    checked={branding.workingHours.enabled}
                    onChange={(e) => updateWorkingHours({ enabled: e.target.checked })}
                    className="h-4 w-4 text-primary focus:ring-ring border-input rounded"
                  />
                  <label htmlFor="enable-hours" className="ml-2 block text-sm text-foreground">
                    Enable working hours (chatbot will show offline message outside these hours)
                  </label>
                </div>

                {branding.workingHours.enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Timezone</label>
                      <select
                        value={branding.workingHours.timezone}
                        onChange={(e) => updateWorkingHours({ timezone: e.target.value })}
                        className="block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      >
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">Eastern Time (US & Canada)</option>
                        <option value="America/Chicago">Central Time (US & Canada)</option>
                        <option value="America/Denver">Mountain Time (US & Canada)</option>
                        <option value="America/Los_Angeles">Pacific Time (US & Canada)</option>
                        <option value="Europe/London">London</option>
                        <option value="Europe/Paris">Paris</option>
                        <option value="Asia/Tokyo">Tokyo</option>
                        <option value="Asia/Shanghai">Shanghai</option>
                        <option value="Australia/Sydney">Sydney</option>
                      </select>
                    </div>

                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-foreground mb-3">Weekly Schedule</h4>
                      <div className="space-y-3">
                        {DAYS.map((day) => (
                          <div key={day} className="flex items-center space-x-4 py-2 border-b border-border">
                            <div className="w-32">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={branding.workingHours.schedule[day]?.enabled || false}
                                  onChange={(e) => updateSchedule(day, 'enabled', e.target.checked)}
                                  className="h-4 w-4 text-primary focus:ring-ring border-input rounded"
                                />
                                <label className="ml-2 block text-sm font-medium text-foreground capitalize">
                                  {day}
                                </label>
                              </div>
                            </div>
                            {branding.workingHours.schedule[day]?.enabled && (
                              <div className="flex items-center space-x-2 flex-1">
                                <input
                                  type="time"
                                  value={branding.workingHours.schedule[day]?.start || '09:00'}
                                  onChange={(e) => updateSchedule(day, 'start', e.target.value)}
                                  className="border border-input rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-ring focus:border-primary"
                                />
                                <span className="text-muted-foreground">to</span>
                                <input
                                  type="time"
                                  value={branding.workingHours.schedule[day]?.end || '17:00'}
                                  onChange={(e) => updateSchedule(day, 'end', e.target.value)}
                                  className="border border-input rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-ring focus:border-primary"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Advanced Tab */}
        {activeTab === 'advanced' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Advanced Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Custom CSS
                  </label>
                  <textarea
                    value={branding.customCSS || ''}
                    onChange={(e) => updateBranding({ customCSS: e.target.value })}
                    maxLength={5000}
                    rows={10}
                    className="block w-full border border-input rounded-md shadow-sm py-2 px-3 font-mono text-sm focus:outline-none focus:ring-ring focus:border-primary"
                    placeholder=".chatbot-container { ... }"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Add custom CSS to further customize the chatbot appearance. Use with caution.
                  </p>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Warning:</strong> Custom CSS can break the chatbot interface if not used properly.
                        Test thoroughly before deploying to production.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save reminder at bottom */}
      {hasChanges && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
          >
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      )}
    </div>
  )
}

// Import missing icon
import { ChatBubbleLeftRightIcon, Cog6ToothIcon } from '@heroicons/react/24/outline'

export default Branding
